import * as Sentry from '@sentry/browser'

type Extension = {
  eid: string,
  page: string,
  detail: string
}
type ResponseCallback = (response: any) => void

export const useChromeExtensionStore = defineStore('chromeExtension', () => {
  const snackbar = useSnackbar()

  // STATE
  const extension = ref<Extension | null>(null)

  // GETTERS
  const eid = computed(() => extension.value?.eid || null)

  // ACTIONS
  const canMessageExtension = () => typeof chrome !== 'undefined' && !!chrome.runtime?.sendMessage

  const login = async () => {
    try {
      if (!eid.value || !canMessageExtension()) { return }
      const { getEffectiveAuthTokenRaw } = useEffectiveAuthToken()
      const token = await getEffectiveAuthTokenRaw()
      if (token) {
        chrome.runtime.sendMessage(eid.value, { greeting: 'emissary-login', jwt: token })
      }
    } catch (error) {
      Sentry.captureException(error)
      snackbar.error('Error during login to Chrome Extension')
    }
  }

  const logout = () => {
    if (!eid.value) { return }
    if (canMessageExtension()) {
      chrome.runtime.sendMessage(eid.value, { greeting: 'emissary-logout' })
    }
    // Keep the eid: the next login in this session has to reach the extension too
    extension.value = { eid: eid.value, page: '', detail: '' }
  }

  const sendCurrentPage = (currentPage: string) => {
    if (!eid.value || !canMessageExtension()) { return }
    chrome.runtime.sendMessage(eid.value, { greeting: 'emissary-set-current-page', currentPage })
  }

  const askForCampaignData = (callback: ResponseCallback) => {
    if (!eid.value || !canMessageExtension()) { return }
    chrome.runtime.sendMessage(eid.value, { greeting: 'get-campaign-data' }, {}, callback)
  }

  const getScreenshotAndHtmlTable = (callback: ResponseCallback) => {
    try {
      if (!eid.value || !canMessageExtension()) { return }
      chrome.runtime.sendMessage(eid.value, { greeting: 'get-screenshot-and-table-html' }, {}, callback)
    } catch (error) {
      Sentry.captureException(error)
      snackbar.error('Error getting information from Chrome Extension')
    }
  }

  return {
    // STATE
    extension,

    // GETTERS
    eid,

    // ACTIONS
    login,
    logout,
    sendCurrentPage,
    askForCampaignData,
    getScreenshotAndHtmlTable
  }
})
